import { IPromise, IQService, IScope } from "angular";
import { StateService } from "@uirouter/angularjs";
import { storage } from "@gtmhub/core/storage";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { hasCurrentUserRole } from "@gtmhub/users";
import { ILanguageMap, localize, Language, filterLanguages } from "@gtmhub/localization";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountService, ISharedOkrsAccountSetting } from "@gtmhub/accounts/accountsService";
import { IStateInit } from "@gtmhub/core/routing";
import { IAccount } from "@gtmhub/core";
import { PricingEditionService } from "@gtmhub/edition-plan-change";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { IFeature, IFeatureAdditionalInfo } from "../models";
import { CustomFieldModalService } from "@gtmhub/customFields/custom-fields-modal.service";
import { LanguagesConfigService } from "@gtmhub/localization/services/languages-config.service";
import languages from "wwwroot/json/languages.json";
import { notificationsCadenceType } from "@gtmhub/shared/filters/notifications-cadence-type.filter";

interface IActionButton {
  title: string;
  action?: "openCustomFieldModal";
  sref?: string;
  kind?: "primary" | "secondary";
}

export interface IConfigurationScope extends IScope {
  account: IAccount;
  limitObjectives: number;
  limitKRs: number;
  integer: RegExp;
  supportedLanguages: ILanguageMap;
  initialLanguage: string;
  indicators: {
    settingLanguage: boolean;
  };

  permissions: {
    initialObjectivePermissions: string;
    initialKRPermissions: string;
    assignObjectivePermissions: string;
    assignKRPermissions: string;
  };

  useLightLayout: boolean;
  actionButtons?: IActionButton[];
  isAdmin: boolean;
  isConfigurationState: boolean;
  accountEditionPermissions: boolean;
  sharedOkrs: ISharedOkrsAccountSetting;
  features: IFeature[];
  methodologyPerSessionFeatureEnabled: boolean;
  methodologyPerSessionEnabled: boolean;
  limitKrsProgress: boolean;
  aggregationEnabled: boolean;
  setLanguage(language: string): void;
  toggle(key: string): void;
  update(key, value): void;
  updateSharedOkrs(key: "objectives" | "krs"): void;
  isPluginState(): boolean;
  onFeatureChange(params: { key: string; value: boolean }): void;
  notificationsCadenceType(text: string): string;
  onActionButtonClick(action: IActionButton["action"]): void;
}

export class ConfigurationCtrl implements IStateInit {
  public static $inject = [
    "$scope",
    "$state",
    "$q",
    "AccountService",
    "AccountResolverService",
    "UIErrorHandlingService",
    "PricingEditionService",
    "AnalyticsService",
    "FeatureToggleV2Service",
    "CustomFieldModalService",
    "LanguagesConfigService",
  ];

  constructor(
    private $scope: IConfigurationScope,
    private $state: StateService,
    private $q: IQService,
    private accountService: AccountService,
    private accountResolverService: AccountResolverService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private pricingEditionService: PricingEditionService,
    private analyticsService: AnalyticsService,
    private featureToggleV2Service: FeatureToggleV2Service,
    private customFieldModalService: CustomFieldModalService,
    private languagesConfigService: LanguagesConfigService
  ) {
    this.$scope.indicators = { settingLanguage: false };
    this.$scope.isConfigurationState = this.$state.current.name.indexOf("configuration") !== -1;
    this.$scope.permissions = {
      assignObjectivePermissions: this.accountService.getAccountSetting("assignObjectivePermissions"),
      initialObjectivePermissions: "",
      assignKRPermissions: this.accountService.getAccountSetting("assignKRPermissions"),
      initialKRPermissions: "",
    };

    if (!this.$scope.permissions.assignObjectivePermissions) {
      this.$scope.permissions.assignObjectivePermissions = "user and team";
    }

    if (!this.$scope.permissions.assignKRPermissions) {
      this.$scope.permissions.assignKRPermissions = "user and team";
    }

    this.$scope.permissions.initialObjectivePermissions = this.$scope.permissions.assignObjectivePermissions;
    this.$scope.permissions.initialKRPermissions = this.$scope.permissions.assignKRPermissions;

    this.$scope.limitObjectives = this.accountService.getAccountSetting("limitObjectives");
    this.$scope.limitKRs = this.accountService.getAccountSetting("limitKRs");
    this.$scope.sharedOkrs = this.accountService.getAccountSetting("sharedOkrs") || ({} as ISharedOkrsAccountSetting);
    this.$scope.methodologyPerSessionEnabled = this.accountService.getAccountSetting("methodologyPerSessionEnabled");
    this.$scope.limitKrsProgress = this.accountService.getAccountSetting("limitKrsProgress");
    this.setAggregation();
    this.$scope.features = [];
    this.$scope.integer = /^\d+$/;

    this.$scope.toggle = this.toggle;
    this.$scope.update = this.update;
    this.$scope.updateSharedOkrs = this.updateSharedOkrs;
    this.$scope.setLanguage = this.setLanguage;
    this.$scope.isPluginState = this.isPluginState;
    this.$scope.notificationsCadenceType = this.notificationsCadenceType;

    this.$scope.isAdmin = hasCurrentUserRole("admin");

    $scope.$watch(
      function () {
        return $state.$current.name;
      },
      function (newValue) {
        if (newValue.indexOf("gtmhub.configuration.customFields") === 0) {
          $scope.useLightLayout = true;
          $scope.actionButtons = [{ title: "create_custom_field", action: "openCustomFieldModal" }];
        } else if (newValue.indexOf("gtmhub.configuration.tags") === 0) {
          $scope.actionButtons = [
            { title: "merge_tags", sref: "gtmhub.configuration.tags._mergeTags.form", kind: "secondary" },
            { title: "create_new_tag", sref: "gtmhub.configuration.tags._createTag.form" },
          ];
          $scope.useLightLayout = true;
        } else {
          $scope.actionButtons = null;
          $scope.useLightLayout = false;
        }
      }
    );

    this.$scope.onFeatureChange = this.onFeatureChange;
    this.$scope.onActionButtonClick = (action: IActionButton["action"]) => this.onActionButtonClick(action);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private notificationsCadenceType = (text: string) => {
    return notificationsCadenceType()(text);
  };

  private setLanguage = (language: Language) => {
    this.$scope.indicators.settingLanguage = true;
    const id = this.$scope.account.id;
    const account = this.$scope.account;
    const updatedAccount = {
      isActive: account.isActive,
      language: language,
      trialEnds: account.trialEnds,
      type: account.type,
    };

    this.accountService.updateAccount(id, updatedAccount).then(
      () => {
        this.$scope.indicators.settingLanguage = false;
        this.accountResolverService.setAccountData(account);
        this.setInitialLanguage(updatedAccount.language);
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "settingLanguage", inline: true })
    );
  };

  private init = (): IPromise<unknown> => {
    this.generateFeaturesCollection();

    return this.$q
      .all({
        account: this.accountService.getAccount(storage.get("accountId")),
        languageMap: this.languagesConfigService.getLanguageMap(),
        methodologyPerSessionFeatureEnabled: this.featureToggleV2Service.isFeatureAvailable("methodology-per-session"),
      })
      .then(
        ({ account, languageMap, methodologyPerSessionFeatureEnabled }) => {
          this.$scope.account = account;
          this.$scope.supportedLanguages = languageMap;
          this.$scope.methodologyPerSessionFeatureEnabled = methodologyPerSessionFeatureEnabled;

          this.setInitialLanguage(account.language);

          return this.pricingEditionService.getAllowedTransitions(account.type).then(
            (plans) => {
              this.$scope.accountEditionPermissions = !!plans.length;
            },
            (err) => {
              this.uiErrorHandlingService.handleModal(err);
            }
          );
        },
        (err) => {
          this.uiErrorHandlingService.handleModal(err);
          this.$scope.supportedLanguages = filterLanguages("italian", languages.languageMap);
        }
      );
  };

  private toggle = (key: string): void => {
    this.$scope[key] = !this.$scope[key];
    this.accountService.setAccountSetting(key, this.$scope[key]);
  };

  private update = (key, value): void => {
    this.accountService.setAccountSetting(key, value);

    if (key === "confidenceScale") {
      this.accountService.setConfidenceScale(value);
    }

    if (key === "limitKrsProgress") {
      this.$scope.limitKrsProgress = value;
    }

    if (key === "aggregation") {
      this.$scope.aggregationEnabled = value;
    }

    this.$scope.permissions.initialObjectivePermissions = this.$scope.permissions.assignObjectivePermissions;
    this.$scope.permissions.initialKRPermissions = this.$scope.permissions.assignKRPermissions;
  };

  private updateSharedOkrs = (key: "objectives" | "krs"): void => {
    if (!this.$scope.sharedOkrs[key]) {
      this.$scope.sharedOkrs[key] = { disabled: true };
    } else {
      this.$scope.sharedOkrs[key].disabled = !this.$scope.sharedOkrs[key].disabled;
    }

    this.analyticsService.track("Shared OKRs Account Setting Changed", { type: key, disabled: this.$scope.sharedOkrs[key].disabled });
    this.accountService.setAccountSetting("sharedOkrs", this.$scope.sharedOkrs);
  };

  private setInitialLanguage = (language: string) => {
    this.$scope.initialLanguage = language;
  };

  private isPluginState = (): boolean => {
    return this.$state.current.name.indexOf("plugin") !== -1;
  };

  private generateFeaturesCollection = (): void => {
    const accountSettingsFeatures = this.accountService.getAccountSetting<{ key: string }>("features");
    const featureAdditionalInfo = this.getFeatureAdditionalInfo();
    Object.keys(featureAdditionalInfo).forEach((k: string) => {
      const additionalInfo: IFeature = featureAdditionalInfo[k];
      additionalInfo.value = accountSettingsFeatures ? accountSettingsFeatures[k] : false;
      this.$scope.features.push(additionalInfo);
    });
  };

  private onFeatureChange = (params: { key: string; value: boolean }): void => {
    const keys = params.key.split(".");
    this.analyticsService.track("Account Feature Changed", { key: keys[1], enabled: params.value });
    this.accountService.setAccountSetting(keys[0], params.value, keys[1] || "").then(() => {
      this.featureToggleV2Service.initialize();
    });
  };

  private getFeatureAdditionalInfo = (): IFeatureAdditionalInfo => {
    return {
      whiteboards: {
        title: localize("whiteboards"),
        description: localize("whiteboards_feature_desc"),
        imgSrc: require("wwwroot/img/feature-settings/whiteboards.svg"),
        key: "features.whiteboards",
        value: true,
      },
      tasks: {
        title: localize("tasks"),
        description: localize("task_feature_desc"),
        imgSrc: require("wwwroot/img/feature-settings/tasks.svg"),
        key: "features.tasks",
        value: true,
      },
      kpis: {
        title: localize("kpis"),
        description: localize("kpis_feature_desc"),
        imgSrc: require("wwwroot/img/feature-settings/kpis.svg"),
        key: "features.kpis",
        value: true,
      },
      badges: {
        title: localize("badges"),
        description: localize("badges_feature_desc"),
        imgSrc: require("wwwroot/img/feature-settings/badges.svg"),
        key: "features.badges",
        value: true,
      },
      gif: {
        title: `${localize("enable")} GIFs`,
        description: localize("display_GIFs_in_comments_and_KR_updates"),
        imgSrc: require("wwwroot/img/feature-settings/gif.svg"),
        key: "features.gif",
        value: true,
      },
      tags: {
        title: localize("tags"),
        description: localize("tags_feature_desc"),
        imgSrc: require("wwwroot/img/feature-settings/tags.svg"),
        key: "features.tags",
        value: true,
      },
    };
  };

  private onActionButtonClick(action: IActionButton["action"]): void {
    if (action === "openCustomFieldModal") {
      this.customFieldModalService.open("");
    }
  }

  private setAggregation(): void {
    const aggregationEnabled = this.accountService.getAccountSetting<boolean>("aggregation");

    this.$scope.aggregationEnabled = aggregationEnabled ?? !aggregationEnabled;
  }
}
